import React from 'react';
import { useScrollAnimation } from './hooks/ScrollAnimation';
import { useTheme } from '../context/ThemeContext';

import khabitsImg from '../assets/Portofolio/khabits.png';

const rulesData = [
  {
    id: 'atomic-habits',
    title: 'Khabits',
    source: 'Atomic Habits - James Clear',
    category: 'HABIT & SYSTEM',
    description: 'Fokus pada siapa yang ingin kita bentuk, bukan sekadar apa yang ingin dicapai. Perubahan kecil 1% setiap hari terakumulasi secara eksponensial.',
    principles: [
      'Sistem lebih penting daripada sekadar tujuan (goals).',
      'Ubah identitas: "Saya adalah penulis", bukan "Saya mencoba menulis".',
      'Jadikan kebiasaan terlihat, menarik, mudah, dan memuaskan.'
    ],
    image: khabitsImg, 
    externalUrl: 'https://khabits.vercel.app/',
    badgeColor: 'bg-blue-600 text-white',
  },
];

const RuleCard = ({ rule, index, isDark }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const delay = (index % 2) * 100;

  const cardBg    = isDark ? 'bg-[#0D1520]'  : 'bg-white';
  const mutedBg   = isDark ? 'bg-[#111C2C]'  : 'bg-gray-50';
  const border    = isDark ? 'border-[#1E3A5F]' : 'border-[var(--border-color)]';
  const titleClr  = isDark ? 'text-white'    : 'text-gray-800';
  const descClr   = isDark ? 'text-slate-400' : 'text-gray-600';
  const princClr  = isDark ? 'text-slate-300' : 'text-gray-700';
  const mutedTxt  = isDark ? 'text-slate-500' : 'text-gray-500';
  const accentClr = isDark ? 'text-blue-400' : 'text-blue-600';
  const btnBg     = isDark
    ? 'bg-[#111C2C] text-slate-200 hover:bg-blue-600 hover:text-white'
    : 'bg-[var(--bg-base)] text-[var(--text-primary)] hover:bg-blue-600 hover:text-white';

  return (
    <div
      ref={ref}
      className={`pixel-border ${cardBg} p-5 flex flex-col justify-between relative group hover:border-blue-500 transition-colors`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.6s ease-out ${delay}ms, transform 0.6s ease-out ${delay}ms`,
      }}
    >
      <div>
        {/* Gambar / Hero Card */}
        <div className={`relative h-48 w-full mb-4 overflow-hidden border-2 ${border}`}>
          <img
            src={rule.image}
            alt={rule.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <span className={`absolute top-2 left-2 text-[10px] pixel-font px-2 py-1 ${rule.badgeColor} border border-[var(--border-color)] shadow-[2px_2px_0px_#000]`}>
            {rule.category}
          </span>
        </div>

        {/* Judul & Sumber Buku */}
        <div className="mb-3">
          <h3 className={`text-lg font-bold mb-1 leading-snug ${titleClr}`}>
            {rule.title}
          </h3>
          <span className={`text-xs font-mono ${accentClr} font-semibold block`}>
            📖 {rule.source}
          </span>
        </div>

        {/* Deskripsi Ringkas */}
        <p className={`text-xs ${descClr} mb-4 leading-relaxed`}>
          {rule.description}
        </p>

        {/* List Prinsip Utama */}
        <div className={`${mutedBg} p-3 border-2 ${border} mb-5`}>
          <span className={`text-[10px] pixel-font block mb-2 ${mutedTxt}`}>
            PRINCIPLES:
          </span>
          <ul className="space-y-1.5 text-xs">
            {rule.principles.map((p, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className={`${accentClr} font-bold`}>►</span>
                <span className={`leading-tight ${princClr}`}>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tombol Menuju Website Khusus */}
      <a
        href={rule.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`pixel-button ${btnBg} py-2.5 px-4 text-xs font-bold flex items-center justify-between w-full mt-2 transition-colors`}
      >
        <span>KUNJUNGI WEBSITE</span>
        <span className="pixel-font text-[10px]">↗</span>
      </a>
    </div>
  );
};

export default function RuleOfLife() {
  const { isDark } = useTheme();
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation({ threshold: 0.1 });

  // Section ini pakai solid muted (bergantian dengan Profile yang pixel-grid)
  const sectionBg = isDark ? 'bg-[#0D1520]' : 'bg-gray-50';
  const titleClr  = isDark ? 'text-white'   : 'text-gray-800';
  const subClr    = isDark ? 'text-slate-400' : 'text-gray-600';

  return (
    <section id="rule-of-life" className={`py-8 md:py-12 px-4 ${sectionBg}`}>
      <div className="container mx-auto max-w-6xl">

        {/* Header Section */}
        <div
          ref={titleRef}
          className="mb-10 md:mb-12 text-center"
          style={{
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? 'translateY(0)' : 'translateY(-24px)',
            transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
          }}
        >
          <div className="inline-block bg-blue-600 text-white px-3 py-1 text-xs pixel-font mb-3 pixel-border">
            PHILOSOPHY & PRINCIPLES
          </div>
          <h2 className={`text-xl md:text-2xl lg:text-3xl font-bold pixel-font tracking-wide mb-4 ${titleClr}`}>
            <span className="glitch-text" data-text="RULE OF LIFE">RULE OF LIFE</span>
          </h2>
          {/* <p className={`text-sm md:text-base max-w-2xl mx-auto leading-relaxed ${subClr}`}>
            Prinsip dan pandangan hidup yang saya pelajari dari berbagai karya literatur,
            diinternalisasi menjadi aturan harian, serta dikembangkan ke dalam proyek mikro tersendiri.
          </p> */}
        </div>

        {/* Grid Rules */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {rulesData.map((rule, index) => (
            <RuleCard key={rule.id} rule={rule} index={index} isDark={isDark} />
          ))}
        </div>

      </div>
    </section>
  );
}
